
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { SeoResult, SeoCompetitor } from "../types";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash, Search, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";

interface CompetitorsTabProps {
  result: SeoResult;
  url: string;
}

export const CompetitorsTab = ({ result, url }: CompetitorsTabProps) => {
  const [competitors, setCompetitors] = useState<SeoCompetitor[]>(result.competitors);
  const [editingCompetitor, setEditingCompetitor] = useState<SeoCompetitor | null>(null);
  const [newCompetitor, setNewCompetitor] = useState<SeoCompetitor>({
    domain: "",
    score: 0,
    traffic: 0,
    keywords: 0,
    backlinks: 0
  });
  const [showBusinessForm, setShowBusinessForm] = useState(false);
  const [businessData, setBusinessData] = useState({
    name: "",
    industry: "",
    targetKeywords: "",
    goals: ""
  });
  const { toast } = useToast();

  const userDomain = url.replace(/^https?:\/\//, '').split('/')[0];
  const userScore = result.score;
  const userBacklinks = result.backlinks;

  const handleEditCompetitor = (competitor: SeoCompetitor) => {
    setEditingCompetitor({...competitor});
  };

  const handleSaveEdit = () => {
    if (editingCompetitor) {
      setCompetitors(competitors.map(c => 
        c.domain === editingCompetitor.domain ? editingCompetitor : c
      ));
      setEditingCompetitor(null);
      toast({
        title: "Competitor Updated",
        description: `${editingCompetitor.domain} has been updated successfully.`
      });
    }
  };

  const handleAddCompetitor = () => {
    if (!newCompetitor.domain) {
      toast({
        title: "Validation Error",
        description: "Please enter a competitor domain name.",
        variant: "destructive"
      });
      return;
    }
    
    if (competitors.some(c => c.domain === newCompetitor.domain)) {
      toast({
        title: "Duplicate Entry",
        description: "This competitor already exists in your list.",
        variant: "destructive"
      });
      return;
    }

    setCompetitors([...competitors, newCompetitor]);
    setNewCompetitor({
      domain: "",
      score: 0,
      traffic: 0,
      keywords: 0,
      backlinks: 0
    });
    
    toast({
      title: "Competitor Added",
      description: `${newCompetitor.domain} has been added to your competitors list.`
    });

    // After adding 3 competitors, show the business data form
    if (competitors.length + 1 >= 3 && !showBusinessForm) {
      setShowBusinessForm(true);
    }
  };

  const handleDeleteCompetitor = (domain: string) => {
    setCompetitors(competitors.filter(c => c.domain !== domain));
    toast({
      title: "Competitor Removed",
      description: `${domain} has been removed from your competitors list.`
    });
  };

  const handleFindCompetitors = () => {
    // Simulate finding competitors
    toast({
      title: "Competitor Analysis Started",
      description: "We're analyzing your industry to find relevant competitors."
    });
    
    setTimeout(() => {
      const newFound: SeoCompetitor[] = [
        {
          domain: `industry-leader-${Math.floor(Math.random() * 100)}.com`,
          score: Math.floor(Math.random() * 20) + 80,
          traffic: Math.floor(Math.random() * 100000) + 100000,
          keywords: Math.floor(Math.random() * 3000) + 5000,
          backlinks: Math.floor(Math.random() * 3000) + 3000
        },
        {
          domain: `rising-competitor-${Math.floor(Math.random() * 100)}.com`,
          score: Math.floor(Math.random() * 20) + 70,
          traffic: Math.floor(Math.random() * 50000) + 50000,
          keywords: Math.floor(Math.random() * 2000) + 3000,
          backlinks: Math.floor(Math.random() * 2000) + 2000
        }
      ];
      
      const uniqueNew = newFound.filter(n => !competitors.some(c => c.domain === n.domain));
      setCompetitors([...competitors, ...uniqueNew]);
      
      toast({
        title: "Competitors Found",
        description: `We found ${uniqueNew.length} new competitors in your industry.`
      });
      
      if (competitors.length + uniqueNew.length >= 3 && !showBusinessForm) {
        setShowBusinessForm(true);
      }
    }, 2000);
  };

  const handleBusinessFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    toast({
      title: "Business Data Saved",
      description: "We'll use this information to enhance your SEO recommendations."
    });
    setShowBusinessForm(false);
  };

  const calculateAverages = () => {
    const avgBacklinks = Math.floor(competitors.reduce((sum, comp) => sum + comp.backlinks, 0) / competitors.length);
    const maxKeywords = Math.max(...competitors.map(c => c.keywords));
    const avgScore = Math.floor(competitors.reduce((sum, comp) => sum + comp.score, 0) / competitors.length);
    const avgTraffic = Math.floor(competitors.reduce((sum, comp) => sum + comp.traffic, 0) / competitors.length);
    
    return { avgBacklinks, maxKeywords, avgScore, avgTraffic };
  };

  const { avgBacklinks, maxKeywords, avgScore, avgTraffic } = calculateAverages();

  return (
    <div className="space-y-6">
      {showBusinessForm && (
        <Card className="border-desert-sunset/30 bg-desert-sunset/5">
          <CardHeader>
            <CardTitle className="flex items-center">
              <ArrowRight className="mr-2 h-5 w-5 text-desert-sunset" />
              Tell Us About Your Business
            </CardTitle>
            <CardDescription>
              Help us create more targeted recommendations based on your specific business needs
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleBusinessFormSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label htmlFor="business-name" className="text-sm font-medium">
                    Business Name
                  </label>
                  <Input 
                    id="business-name" 
                    value={businessData.name}
                    onChange={(e) => setBusinessData({...businessData, name: e.target.value})}
                    placeholder="Your company name"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="industry" className="text-sm font-medium">
                    Industry
                  </label>
                  <Input 
                    id="industry" 
                    value={businessData.industry}
                    onChange={(e) => setBusinessData({...businessData, industry: e.target.value})}
                    placeholder="e.g. E-commerce, SaaS, Healthcare"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label htmlFor="target-keywords" className="text-sm font-medium">
                  Target Keywords (separated by commas)
                </label>
                <Textarea 
                  id="target-keywords" 
                  value={businessData.targetKeywords}
                  onChange={(e) => setBusinessData({...businessData, targetKeywords: e.target.value})}
                  placeholder="e.g. digital marketing, SEO services, content strategy"
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="business-goals" className="text-sm font-medium">
                  Business Goals
                </label>
                <Textarea 
                  id="business-goals" 
                  value={businessData.goals}
                  onChange={(e) => setBusinessData({...businessData, goals: e.target.value})}
                  placeholder="What are your primary objectives for SEO? E.g. increase leads, boost sales, improve brand awareness"
                  rows={3}
                />
              </div>
              <Button type="submit" className="w-full md:w-auto bg-desert-gradient hover:opacity-90">
                Save Business Information
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Competitor Analysis</CardTitle>
            <CardDescription>How your site compares to top competitors in your industry</CardDescription>
          </div>
          <div className="flex space-x-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleFindCompetitors}
              className="hidden sm:flex"
            >
              <Search className="mr-2 h-4 w-4" />
              Find Competitors
            </Button>
            <Dialog>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-desert-sunset hover:bg-desert-sunset/90">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Competitor
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Competitor</DialogTitle>
                  <DialogDescription>
                    Enter information about a competitor to add them to your analysis.
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="space-y-2">
                    <label htmlFor="domain" className="text-sm font-medium">Domain Name</label>
                    <Input 
                      id="domain" 
                      value={newCompetitor.domain}
                      onChange={(e) => setNewCompetitor({...newCompetitor, domain: e.target.value})}
                      placeholder="e.g. competitor.com"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label htmlFor="score" className="text-sm font-medium">SEO Score</label>
                      <Input 
                        id="score" 
                        type="number"
                        value={newCompetitor.score || ""}
                        onChange={(e) => setNewCompetitor({...newCompetitor, score: parseInt(e.target.value) || 0})}
                        placeholder="0-100"
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="traffic" className="text-sm font-medium">Est. Traffic</label>
                      <Input 
                        id="traffic" 
                        type="number"
                        value={newCompetitor.traffic || ""}
                        onChange={(e) => setNewCompetitor({...newCompetitor, traffic: parseInt(e.target.value) || 0})}
                        placeholder="Monthly visits"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label htmlFor="keywords" className="text-sm font-medium">Keywords</label>
                      <Input 
                        id="keywords" 
                        type="number"
                        value={newCompetitor.keywords || ""}
                        onChange={(e) => setNewCompetitor({...newCompetitor, keywords: parseInt(e.target.value) || 0})}
                        placeholder="Ranking keywords"
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="backlinks" className="text-sm font-medium">Backlinks</label>
                      <Input 
                        id="backlinks" 
                        type="number"
                        value={newCompetitor.backlinks || ""}
                        onChange={(e) => setNewCompetitor({...newCompetitor, backlinks: parseInt(e.target.value) || 0})}
                        placeholder="Total backlinks"
                      />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={handleAddCompetitor} className="bg-desert-sunset hover:bg-desert-sunset/90">
                    Add Competitor
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4">Domain</th>
                    <th className="text-center py-3 px-4">SEO Score</th>
                    <th className="text-center py-3 px-4">Est. Traffic</th>
                    <th className="text-center py-3 px-4">Keywords</th>
                    <th className="text-center py-3 px-4">Backlinks</th>
                    <th className="text-center py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b bg-desert-sunset/10">
                    <td className="py-3 px-4 font-medium">{userDomain} (You)</td>
                    <td className="py-3 px-4 text-center font-medium">{userScore}/100</td>
                    <td className="py-3 px-4 text-center">{(Math.floor(Math.random() * 60) + 20) * 1000}</td>
                    <td className="py-3 px-4 text-center">{(Math.floor(Math.random() * 30) + 20) * 100}</td>
                    <td className="py-3 px-4 text-center">{userBacklinks.toLocaleString()}</td>
                    <td className="py-3 px-4 text-center">-</td>
                  </tr>
                  {competitors.map((competitor, index) => (
                    <tr key={index} className="border-b hover:bg-muted/50">
                      <td className="py-3 px-4 font-medium">{competitor.domain}</td>
                      <td className="py-3 px-4 text-center">{competitor.score}/100</td>
                      <td className="py-3 px-4 text-center">{competitor.traffic.toLocaleString()}</td>
                      <td className="py-3 px-4 text-center">{competitor.keywords.toLocaleString()}</td>
                      <td className="py-3 px-4 text-center">{competitor.backlinks.toLocaleString()}</td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex justify-center space-x-2">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <Pencil className="h-4 w-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Edit Competitor</DialogTitle>
                                <DialogDescription>
                                  Update information about {competitor.domain}
                                </DialogDescription>
                              </DialogHeader>
                              {editingCompetitor && (
                                <div className="grid gap-4 py-4">
                                  <div className="space-y-2">
                                    <label htmlFor="edit-domain" className="text-sm font-medium">Domain Name</label>
                                    <Input 
                                      id="edit-domain" 
                                      value={editingCompetitor.domain}
                                      onChange={(e) => setEditingCompetitor({...editingCompetitor, domain: e.target.value})}
                                      disabled
                                    />
                                  </div>
                                  <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                      <label htmlFor="edit-score" className="text-sm font-medium">SEO Score</label>
                                      <Input 
                                        id="edit-score" 
                                        type="number"
                                        value={editingCompetitor.score || ""}
                                        onChange={(e) => setEditingCompetitor({...editingCompetitor, score: parseInt(e.target.value) || 0})}
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <label htmlFor="edit-traffic" className="text-sm font-medium">Est. Traffic</label>
                                      <Input 
                                        id="edit-traffic" 
                                        type="number"
                                        value={editingCompetitor.traffic || ""}
                                        onChange={(e) => setEditingCompetitor({...editingCompetitor, traffic: parseInt(e.target.value) || 0})}
                                      />
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                      <label htmlFor="edit-keywords" className="text-sm font-medium">Keywords</label>
                                      <Input 
                                        id="edit-keywords" 
                                        type="number"
                                        value={editingCompetitor.keywords || ""}
                                        onChange={(e) => setEditingCompetitor({...editingCompetitor, keywords: parseInt(e.target.value) || 0})}
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <label htmlFor="edit-backlinks" className="text-sm font-medium">Backlinks</label>
                                      <Input 
                                        id="edit-backlinks" 
                                        type="number"
                                        value={editingCompetitor.backlinks || ""}
                                        onChange={(e) => setEditingCompetitor({...editingCompetitor, backlinks: parseInt(e.target.value) || 0})}
                                      />
                                    </div>
                                  </div>
                                </div>
                              )}
                              <DialogFooter>
                                <Button
                                  onClick={() => {
                                    handleEditCompetitor(competitor);
                                    handleSaveEdit();
                                  }}
                                  className="bg-desert-sunset hover:bg-desert-sunset/90"
                                >
                                  Save Changes
                                </Button>
                              </DialogFooter>
                            </DialogContent>
                          </Dialog>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 text-destructive hover:text-destructive/90 hover:bg-destructive/10"
                            onClick={() => handleDeleteCompetitor(competitor.domain)}
                          >
                            <Trash className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="bg-muted/30 p-4 rounded-lg">
              <h4 className="font-medium mb-2">Competitive Analysis Insights:</h4>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Your competitors have an average of {avgBacklinks.toLocaleString()} backlinks compared to your {userBacklinks.toLocaleString()}</li>
                <li>Your top competitor ranks for {maxKeywords.toLocaleString()} keywords</li>
                <li>Your site's SEO score is {userScore < avgScore ? "below" : "above"} the industry average of {avgScore}</li>
                <li>Competitors are getting {avgTraffic.toLocaleString()} monthly visits on average</li>
              </ul>
            </div>
            
            <Button 
              variant="outline" 
              onClick={handleFindCompetitors}
              className="w-full sm:hidden"
            >
              <Search className="mr-2 h-4 w-4" />
              Find Competitors
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CompetitorsTab;
